/**
 * Validation Utilities
 * Reusable validation functions for forms across the application
 */

import {
  validateEmail,
  validatePhone,
  validatePasswordPolicy,
  validateValue,
} from "@livingsyncs/types";
import type { ValidationRules } from "@livingsyncs/types";

import { calculateAge } from "./residentFormatters";
// Regular Expressions
const ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/;
const URL_REGEX = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;

/**
 * Phone/email format validators come from the shared single source in
 * `@livingsyncs/types` — the same `isValidPhone`/`isValidEmail` predicates the
 * backend Zod refines use, so the client pre-filter and server acceptance
 * cannot drift (the old local copies did: this file rejected `+1`-prefixed
 * numbers the server accepted, and its email regex was laxer than the
 * server's).
 */
export { validateEmail, validatePhone };

/**
 * Validate required email
 */
export function validateRequiredEmail(
  value: string | null | undefined,
): string | null {
  if (!value) return "Email is required";
  return validateEmail(value);
}

/**
 * Format a phone number as (XXX) XXX-XXXX while typing.
 * Strips non-digits, caps at 10, applies US format progressively.
 */
export function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Format an SSN as XXX-XX-XXXX while typing.
 * Strips non-digits, caps at 9, applies dashes progressively.
 */
export function formatSSN(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

/**
 * Validate SSN — must be exactly 9 digits (raw or XXX-XX-XXXX formatted).
 */
export function validateSSN(value: string | null | undefined): string | null {
  if (!value) return null; // optional field
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 9) return "SSN must be 9 digits";
  return null;
}

/**
 * Validate required phone number
 */
export function validateRequiredPhone(
  value: string | null | undefined,
): string | null {
  if (!value) return "Phone number is required";
  return validatePhone(value);
}

/**
 * Validate ZIP code (US format)
 * Accepts: 12345 or 12345-6789
 */
export function validateZipCode(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  if (!ZIP_CODE_REGEX.test(value)) {
    return "Please enter a valid ZIP code (12345 or 12345-6789)";
  }
  return null;
}

/**
 * Validate URL
 */
export function validateUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  if (!URL_REGEX.test(value)) {
    return "Please enter a valid URL";
  }
  return null;
}

/**
 * Validate required field
 */
export function validateRequired(fieldName: string) {
  return (value: unknown) => {
    if (value === null || value === undefined || value === "") {
      return `${fieldName} is required`;
    }
    return null;
  };
}

/**
 * Validate minimum length
 */
export function validateMinLength(min: number, fieldName: string) {
  return (value: string | null | undefined) => {
    if (!value) return null;
    if (value.length < min) {
      return `${fieldName} must be at least ${min} characters`;
    }
    return null;
  };
}

/**
 * Validate maximum length
 */
export function validateMaxLength(max: number, fieldName: string) {
  return (value: string | null | undefined) => {
    if (!value) return null;
    if (value.length > max) {
      return `${fieldName} must not exceed ${max} characters`;
    }
    return null;
  };
}

/**
 * Validate number range
 */
export function validateNumberRange(
  min: number,
  max: number,
  fieldName: string,
) {
  return (value: number | null | undefined) => {
    if (value === null || value === undefined) return null;
    if (value < min || value > max) {
      return `${fieldName} must be between ${min} and ${max}`;
    }
    return null;
  };
}

/**
 * Validate positive number
 */
export function validatePositiveNumber(fieldName: string) {
  return (value: number | null | undefined) => {
    if (value === null || value === undefined) return null;
    if (value <= 0) {
      return `${fieldName} must be a positive number`;
    }
    return null;
  };
}

/**
 * Validate password strength against the canonical policy in
 * `@livingsyncs/types` (kept in sync with the Cognito user pool policy):
 * at least 12 characters with uppercase, lowercase, number, and symbol.
 */
export function validatePassword(
  value: string | null | undefined,
): string | null {
  return validatePasswordPolicy(value);
}

/**
 * Validate password confirmation matches
 */
export function validatePasswordMatch(password: string) {
  return (confirmPassword: string | null | undefined) => {
    if (!confirmPassword) return "Please confirm your password";
    if (confirmPassword !== password) {
      return "Passwords do not match";
    }
    return null;
  };
}

/**
 * Validate date is not in the future
 */
export function validateNotFutureDate(fieldName: string) {
  return (value: Date | null | undefined) => {
    if (!value) return null;
    if (value > new Date()) {
      return `${fieldName} cannot be in the future`;
    }
    return null;
  };
}

/**
 * Validate date is not in the past
 */
export function validateNotPastDate(fieldName: string) {
  return (value: Date | null | undefined) => {
    if (!value) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (value < today) {
      return `${fieldName} cannot be in the past`;
    }
    return null;
  };
}

/**
 * Validate age (date of birth)
 * Ensures person is at least minAge years old
 */
export function validateMinAge(minAge: number) {
  return (dob: Date | null | undefined) => {
    if (!dob) return null;

    const age = calculateAge(dob);

    if (age < minAge) {
      return `Person must be at least ${minAge} years old`;
    }

    return null;
  };
}

/**
 * Build a Mantine `validate:` object from a shared field-rules map (US-080).
 *
 * The rule maps live in `@livingsyncs/types` (`fieldRules/`) and are the same
 * constants the backend Zod schemas read their bounds from, so client and
 * server constraints cannot drift. Messages come from the shared
 * `validateValue`, matching what the dynamic form engine shows.
 *
 * Compose order: spread the generated validators FIRST, then list bespoke
 * per-field validators after — later keys override the generated ones.
 *
 * ```ts
 * validate: {
 *   ...rulesToMantineValidate(INCIDENT_FIELD_RULES, INCIDENT_FIELD_LABELS),
 *   resident_id: (value, values) => ...bespoke cross-field rule...,
 * }
 * ```
 */
export function rulesToMantineValidate<
  T extends Record<string, ValidationRules>,
>(
  rules: T,
  labels: Partial<Record<keyof T, string>> = {},
): Record<keyof T, (value: unknown) => string | null> {
  const validate = {} as Record<keyof T, (value: unknown) => string | null>;
  for (const field of Object.keys(rules) as Array<keyof T>) {
    const label = labels[field] ?? String(field);
    validate[field] = (value: unknown) =>
      validateValue(label, rules[field], value)[0] ?? null;
  }
  return validate;
}

/**
 * Compose multiple validators
 */
export function composeValidators(
  ...validators: Array<(value: unknown) => string | null>
) {
  return (value: unknown) => {
    for (const validator of validators) {
      const error = validator(value);
      if (error) return error;
    }
    return null;
  };
}

/**
 * Validation helper for conditional validation
 * Only validates if condition is true
 */
export function validateIf(
  condition: boolean,
  validator: (value: unknown) => string | null,
) {
  return (value: unknown) => {
    if (!condition) return null;
    return validator(value);
  };
}
